import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Close, ShoppingCart, AttachFile, CheckCircle } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { StandaloneExam, StandaloneExamPurchaseFormData } from '../../types';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface StandaloneExamPurchaseModalProps {
  open: boolean;
  onClose: () => void;
  exam: StandaloneExam;
  onSuccess?: () => void;
}

type FormData = Omit<StandaloneExamPurchaseFormData, 'payment_proof' | 'standalone_exam_id'>;

const StandaloneExamPurchaseModal: React.FC<StandaloneExamPurchaseModalProps> = ({
  open,
  onClose,
  exam,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const defaultEmail = user?.email ?? '';
  const defaultCompleteName = user ? [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || '' : '';

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      email: defaultEmail,
      complete_name: defaultCompleteName,
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        email: user?.email ?? '',
        complete_name: user ? [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || '' : '',
      });
    }
  }, [open, user?.email, user?.first_name, user?.last_name, reset]);

  const isMonthly = exam.subscription_type === 'monthly';
  const subscriptionMonths = watch('subscription_months');

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      setError('');
      if (!selectedFile) {
        setError('Please select a payment proof file');
        return;
      }
      const purchaseData: StandaloneExamPurchaseFormData = {
        ...data,
        standalone_exam_id: exam.id,
        payment_proof: selectedFile,
        subscription_months: isMonthly ? (data.subscription_months ?? null) : null,
      };
      await apiService.createStandaloneExamPurchase(purchaseData);
      setShowSuccessModal(true);
      reset();
      setSelectedFile(null);
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Purchase request failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    setSelectedFile(null);
    setShowSuccessModal(false);
    onClose();
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onClose();
    onSuccess?.();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.files?.[0] || null);
  };

  return (
    <>
      <Dialog
        open={showSuccessModal}
        onClose={handleSuccessClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(16, 185, 129, 0.3)',
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 5, px: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
            }}
          >
            <CheckCircle sx={{ fontSize: '3rem', color: 'white' }} />
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Purchase Submitted Successfully!
          </Typography>
          <Alert
            severity="info"
            sx={{
              mt: 3,
              mb: 3,
              textAlign: 'left',
              borderRadius: 2,
              backgroundColor: 'rgba(16, 185, 129, 0.05)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              What&apos;s Next?
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
              Your exam purchase has been submitted. Access will be granted after verification, and you will be notified via email.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            Our team will review your payment proof and verify your purchase. Once verified, you can start the exam from this page.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSuccessClose}
            sx={{
              px: 6,
              py: 1.5,
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)' },
            }}
          >
            Got It!
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open && !showSuccessModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '95vh',
            overflow: 'auto',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingCart sx={{ color: '#10b981', fontSize: 24 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Purchase: {exam.title}
              </Typography>
              <Typography variant="body1" sx={{ color: '#10b981', fontWeight: 600 }}>
                ${exam.price}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ px: 3, py: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              required
              label="Email Address"
              type="email"
              disabled={!!user}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message || (user ? 'Using your account email' : undefined)}
            />

            <TextField
              fullWidth
              required
              label="Full Name"
              sx={{ mt: 2 }}
              {...register('complete_name', { required: 'Full name is required' })}
              error={!!errors.complete_name}
              helperText={errors.complete_name?.message}
              placeholder="Your full name"
            />

            <TextField
              fullWidth
              required
              label="WhatsApp Number"
              sx={{ mt: 2 }}
              {...register('whatsapp_number', { required: 'WhatsApp number is required' })}
              error={!!errors.whatsapp_number}
              helperText={
                errors.whatsapp_number?.message ||
                'Please fill this with your actual WhatsApp number so we can contact you if needed'
              }
              placeholder="+670 XXXX XXXX"
            />

            <FormControl fullWidth sx={{ mt: 2 }} error={!!errors.payment_method} required>
              <InputLabel required>Payment Method</InputLabel>
              <Select
                {...register('payment_method', { required: 'Payment method is required' })}
                label="Payment Method"
                error={!!errors.payment_method}
              >
                <MenuItem value="mosan">Mosan</MenuItem>
                <MenuItem value="tpay">Tpay</MenuItem>
                <MenuItem value="transfer_bank">Bank Transfer</MenuItem>
                <MenuItem value="diretamente">Direct Payment</MenuItem>
              </Select>
              {errors.payment_method && (
                <FormHelperText>{errors.payment_method.message}</FormHelperText>
              )}
            </FormControl>

            {isMonthly && (
              <TextField
                fullWidth
                required
                type="number"
                label="Subscription Duration (Months)"
                sx={{ mt: 2 }}
                {...register('subscription_months', {
                  required: 'Subscription duration is required for monthly plans',
                  min: { value: 1, message: 'Minimum 1 month' },
                  max: { value: 24, message: 'Maximum 24 months' },
                  valueAsNumber: true,
                })}
                error={!!errors.subscription_months}
                helperText={errors.subscription_months?.message}
                InputProps={{
                  inputProps: { min: 1, max: 24 },
                  endAdornment: <InputAdornment position="end">months</InputAdornment>,
                }}
              />
            )}

            {isMonthly && subscriptionMonths && !isNaN(Number(subscriptionMonths)) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(16, 185, 129, 0.1)', borderRadius: 1, border: '1px solid #10b981' }}>
                <Typography variant="subtitle1" sx={{ color: '#059669', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Amount:</span>
                  <span>${((parseFloat(String(exam.price).replace(/[^0-9.]/g, '')) || 0) * Number(subscriptionMonths)).toFixed(2)}</span>
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Payment Proof (Image) <Typography component="span" color="error.main">*</Typography>
              </Typography>
              <Box
                sx={{
                  border: '2px dashed #10b981',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: 'rgba(16, 185, 129, 0.05)',
                }}
              >
                <AttachFile sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ width: '100%', padding: '8px' }}
                />
                {selectedFile ? (
                  <Typography variant="caption" sx={{ color: 'success.main', mt: 1, display: 'block' }}>
                    Selected: {selectedFile.name}
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
                    Please select a payment proof file
                  </Typography>
                )}
              </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                After submitting, your payment will be reviewed. You will receive an email once verified and can then start the exam from this page.
              </Typography>
            </Alert>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={loading} variant="outlined" sx={{ borderColor: '#10b981', color: '#10b981' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}
            >
              {loading ? 'Submitting...' : 'Submit Purchase'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default StandaloneExamPurchaseModal;
